import React, { useEffect, useState, useCallback } from 'react';
import {
  startPKCEFlow,
  refreshAccessToken,
  fetchUserInfo,
  setPrimaryLanguage,
} from '../services/SSO';
import SectionLoadingIndicator from '../components/molecules/SectionLoadingIndicator';
import Logo from '../components/atoms/Logo';
import { userDetailSegment } from '../store/globalStore/stateSegments/userDetailSegment';
import store from '../store/markettingContentGeneration';
import { encodeToBase64 } from '../utils/encodeString';
import { ListUserType } from '../types/Users';
import { initializeFeatureFlags } from '../store/globalStore/actions/featureFlagsActions';

interface SSOProviderProps {
  children: React.ReactNode;
}

const testTime = localStorage.getItem('refresh-token-time');

const FIFTY_FIVE_MINUTES = testTime ? parseInt(testTime) : 1000 * 60 * 55;

export const SSOProvider: React.FC<SSOProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  /**
   * Updates the user detail segment in the store with the client ID and its Base64 encoded value.
   *
   * @param {ListUserType | null} userInfo - The user information object containing the client ID.
   */
  function updateUserDetails(userInfo: ListUserType | null): void {
    if (userInfo) {
      const { clientId } = userInfo;
      store.set(userDetailSegment, prev => ({
        ...prev,
        userNtid: clientId,
        userNtidEncoded: encodeToBase64(clientId) ?? '',
      }));
    }
  }

  // New function to handle automatic token refresh
  const autoRefreshToken = useCallback(async () => {
    const expiresIn = sessionStorage.getItem('pfz-cggenai-token-expiry');
    const refreshToken = sessionStorage.getItem('pfz-cggenai-token-refresh');

    // Check if we have a refresh token and expiresIn
    if (refreshToken && expiresIn) {
      try {
        await refreshAccessToken(refreshToken);
        setIsLoggedIn(true);
        setTimeout(() => autoRefreshToken(), FIFTY_FIVE_MINUTES);
      } catch {
        startPKCEFlow();
      }
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      setIsLoggedIn(true);
      setPrimaryLanguage('en');
      (async () => {
        const userInfo = await fetchUserInfo();
        updateUserDetails(userInfo);
        await initializeFeatureFlags();
      })();
    } else {
      const init = async () => {
        const accessToken = sessionStorage.getItem('pfz-cggenai-token-access');
        const refreshToken = sessionStorage.getItem(
          'pfz-cggenai-token-refresh'
        );
        const expiresAt = sessionStorage.getItem('pfz-cggenai-token-expiry');

        if (!accessToken && !refreshToken) {
          await startPKCEFlow();
        } else if (accessToken && Date.now() > parseInt(expiresAt || '0')) {
          try {
            await refreshAccessToken(refreshToken || '');
            setIsLoggedIn(true);
          } catch {
            await startPKCEFlow();
          }
        } else {
          // if (isEmpty(sessionStorage.getItem('pfz-cggenai-user-givenname'))) {
          const userInfo = await fetchUserInfo();
          updateUserDetails(userInfo);
          // }
          setIsLoggedIn(true);
        }

        // Initialize feature flags at boot time
        await initializeFeatureFlags();

        // Call autoRefreshToken after one hour - 5 minutes = 55 minutes as expiry of access is 3600 thats one hour
        setTimeout(() => autoRefreshToken(), FIFTY_FIVE_MINUTES);
      };

      init();
    }
  }, [autoRefreshToken]);

  return isLoggedIn ? (
    <div>{children}</div>
  ) : (
    <div className='preloader_wrapper'>
      <Logo />
      <SectionLoadingIndicator text='Getting things ready...' />
    </div>
  );
};
SSOProvider.displayName = 'SSOProvider';
